import React from 'react';

/**
 * Real brand logos via Iconify CDN (MIT-licensed, retina-crisp SVG).
 *   - `logos:*` set → multi-color brand-accurate SVGs (preferred)
 *   - `simple-icons:*` set → monochrome, tinted with ?color=<hex>
 *
 * v34.10 (Feb 2026): replaced hand-drawn approximations + initial-circle
 * fallbacks with the real brand SVGs. Slugs verified against
 * api.iconify.design Feb 2026.
 */

// Iconify URL helper — `id` is "<set>:<name>", optional hex tint for monochrome.
const ICON = (id, hex) => {
  const url = `https://api.iconify.design/${id}.svg`;
  return hex ? `${url}?color=%23${hex}` : url;
};

const BrandLogo = ({ src, alt }) => (
  <img
    src={src}
    alt={alt}
    width={28}
    height={28}
    loading="lazy"
    className="w-7 h-7 object-contain"
    onError={(e) => { e.currentTarget.style.display = 'none'; }}
  />
);

// Per-provider real-logo factory components
export const AWSLogo          = () => <BrandLogo src={ICON('logos:aws')}                                 alt="AWS" />;
export const AzureLogo        = () => <BrandLogo src={ICON('logos:microsoft-azure')}                     alt="Microsoft Azure" />;
export const GCPLogo          = () => <BrandLogo src={ICON('logos:google-cloud')}                        alt="Google Cloud" />;
export const SnowflakeLogo    = () => <BrandLogo src={ICON('logos:snowflake-icon')}                      alt="Snowflake" />;
export const OracleLogo       = () => <BrandLogo src={ICON('logos:oracle')}                              alt="Oracle Cloud" />;
export const IBMLogo          = () => <BrandLogo src={ICON('logos:ibm')}                                 alt="IBM Cloud" />;
export const AlibabaLogo      = () => <BrandLogo src={ICON('simple-icons:alibabacloud', 'FF6A00')}       alt="Alibaba Cloud" />;
export const DatabricksLogo   = () => <BrandLogo src={ICON('simple-icons:databricks', 'FF3621')}         alt="Databricks" />;
export const DigitalOceanLogo = () => <BrandLogo src={ICON('simple-icons:digitalocean', '0080FF')}       alt="DigitalOcean" />;
export const CloudflareLogo   = () => <BrandLogo src={ICON('logos:cloudflare')}                          alt="Cloudflare" />;
export const HetznerLogo      = () => <BrandLogo src={ICON('simple-icons:hetzner', 'D50C2D')}            alt="Hetzner" />;
export const VultrLogo        = () => <BrandLogo src={ICON('simple-icons:vultr', '007BFC')}              alt="Vultr" />;
export const OVHLogo          = () => <BrandLogo src={ICON('simple-icons:ovh', '123F6D')}                alt="OVHcloud" />;
export const WasabiLogo       = () => <BrandLogo src={ICON('simple-icons:wasabi', '01CD3E')}             alt="Wasabi" />;
export const LinodeLogo       = () => <BrandLogo src={ICON('simple-icons:linode', '00A95C')}             alt="Linode / Akamai" />;

// Unified cloud provider definitions
export const CLOUD_PROVIDERS = [
  {
    id: 'aws',
    name: 'AWS',
    fullName: 'Amazon Web Services',
    LogoComponent: AWSLogo,
    borderColor: 'border-orange-500',
    bgColor: 'bg-orange-50 dark:bg-orange-900/20',
    textColor: 'text-orange-600 dark:text-orange-400',
    accentColor: 'orange',
    tier: 'major',
    live: true,
  },
  {
    id: 'azure',
    name: 'Azure',
    fullName: 'Microsoft Azure',
    LogoComponent: AzureLogo,
    borderColor: 'border-blue-500',
    bgColor: 'bg-blue-50 dark:bg-blue-900/20',
    textColor: 'text-blue-600 dark:text-blue-400',
    accentColor: 'blue',
    tier: 'major',
    live: true,
  },
  {
    id: 'gcp',
    name: 'GCP',
    fullName: 'Google Cloud Platform',
    LogoComponent: GCPLogo,
    borderColor: 'border-red-500',
    bgColor: 'bg-red-50 dark:bg-red-900/20',
    textColor: 'text-red-600 dark:text-red-400',
    accentColor: 'red',
    tier: 'major',
    live: true,
  },
  {
    id: 'snowflake',
    name: 'Snowflake',
    fullName: 'Data Cloud & Warehouse',
    LogoComponent: SnowflakeLogo,
    borderColor: 'border-cyan-500',
    bgColor: 'bg-cyan-50 dark:bg-cyan-900/20',
    textColor: 'text-cyan-600 dark:text-cyan-400',
    accentColor: 'cyan',
    tier: 'major',
    live: true,
  },
  {
    id: 'oracle',
    name: 'Oracle Cloud',
    fullName: 'Oracle Cloud Infrastructure',
    LogoComponent: OracleLogo,
    borderColor: 'border-red-600',
    bgColor: 'bg-red-50 dark:bg-red-900/20',
    textColor: 'text-red-600 dark:text-red-400',
    accentColor: 'red',
    tier: 'enterprise',
    live: false,
  },
  {
    id: 'ibm',
    name: 'IBM Cloud',
    fullName: 'IBM Cloud',
    LogoComponent: IBMLogo,
    borderColor: 'border-blue-700',
    bgColor: 'bg-blue-50 dark:bg-blue-900/20',
    textColor: 'text-blue-700 dark:text-blue-400',
    accentColor: 'blue',
    tier: 'enterprise',
    live: false,
  },
  {
    id: 'alibaba',
    name: 'Alibaba Cloud',
    fullName: 'Alibaba Cloud (Aliyun)',
    LogoComponent: AlibabaLogo,
    borderColor: 'border-orange-600',
    bgColor: 'bg-orange-50 dark:bg-orange-900/20',
    textColor: 'text-orange-600 dark:text-orange-400',
    accentColor: 'orange',
    tier: 'enterprise',
    live: false,
  },
  {
    id: 'databricks',
    name: 'Databricks',
    fullName: 'Data & AI Platform',
    LogoComponent: DatabricksLogo,
    borderColor: 'border-red-500',
    bgColor: 'bg-red-50 dark:bg-red-900/20',
    textColor: 'text-red-500 dark:text-red-400',
    accentColor: 'red',
    tier: 'data',
    live: true,
  },
  {
    id: 'digitalocean',
    name: 'DigitalOcean',
    fullName: 'DigitalOcean Cloud',
    LogoComponent: DigitalOceanLogo,
    borderColor: 'border-blue-500',
    bgColor: 'bg-blue-50 dark:bg-blue-900/20',
    textColor: 'text-blue-500 dark:text-blue-400',
    accentColor: 'blue',
    tier: 'growth',
    live: true,
  },
  {
    id: 'cloudflare',
    name: 'Cloudflare',
    fullName: 'Workers, R2, CDN',
    LogoComponent: CloudflareLogo,
    borderColor: 'border-orange-500',
    bgColor: 'bg-orange-50 dark:bg-orange-900/20',
    textColor: 'text-orange-500 dark:text-orange-400',
    accentColor: 'orange',
    tier: 'growth',
    live: false,
  },
  {
    id: 'linode',
    name: 'Linode/Akamai',
    fullName: 'Akamai Cloud Computing',
    LogoComponent: LinodeLogo,
    borderColor: 'border-green-500',
    bgColor: 'bg-green-50 dark:bg-green-900/20',
    textColor: 'text-green-600 dark:text-green-400',
    accentColor: 'green',
    tier: 'growth',
    live: false,
  },
  {
    id: 'hetzner',
    name: 'Hetzner',
    fullName: 'Hetzner Cloud (EU)',
    LogoComponent: HetznerLogo,
    borderColor: 'border-red-500',
    bgColor: 'bg-red-50 dark:bg-red-900/20',
    textColor: 'text-red-500 dark:text-red-400',
    accentColor: 'red',
    tier: 'niche',
    live: false,
  },
  {
    id: 'vultr',
    name: 'Vultr',
    fullName: 'Vultr Cloud Compute',
    LogoComponent: VultrLogo,
    borderColor: 'border-blue-500',
    bgColor: 'bg-blue-50 dark:bg-blue-900/20',
    textColor: 'text-blue-500 dark:text-blue-400',
    accentColor: 'blue',
    tier: 'niche',
    live: false,
  },
  {
    id: 'ovh',
    name: 'OVHcloud',
    fullName: 'OVHcloud (EU)',
    LogoComponent: OVHLogo,
    borderColor: 'border-indigo-600',
    bgColor: 'bg-indigo-50 dark:bg-indigo-900/20',
    textColor: 'text-indigo-600 dark:text-indigo-400',
    accentColor: 'indigo',
    tier: 'niche',
    live: false,
  },
  {
    id: 'wasabi',
    name: 'Wasabi',
    fullName: 'Hot Cloud Storage',
    LogoComponent: WasabiLogo,
    borderColor: 'border-green-500',
    bgColor: 'bg-green-50 dark:bg-green-900/20',
    textColor: 'text-green-500 dark:text-green-400',
    accentColor: 'green',
    tier: 'niche',
    live: false,
  },
];
